import { StarIcon } from '@heroicons/react/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import clsxm from '@/lib/clsxm';

interface NavPinButtonProps {
  slug: string;
  isFavorite: boolean;
  onToggleFavorite: (slug: string) => void;
  disabled?: boolean;
  showOnHover?: boolean; // Only show button on parent hover
  alwaysShowIfFavorite?: boolean; // Always show if favorited (for flyout)
}

const NavPinButton: React.FC<NavPinButtonProps> = ({
  slug,
  isFavorite,
  onToggleFavorite,
  disabled = false,
  showOnHover = false,
  alwaysShowIfFavorite = false,
}) => {
  const { t } = useLocale('dashboard');
  const [isHovered, setIsHovered] = useState(false);

  if (disabled) {
    return null;
  }

  // Determine visibility class
  const getVisibilityClass = () => {
    if (alwaysShowIfFavorite && isFavorite) {
      return 'opacity-100'; // Always show if favorited in flyout
    }
    if (showOnHover) {
      return 'opacity-0 group-hover:opacity-100'; // Show on parent hover
    }
    return 'opacity-100'; // Always show
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite(slug);
  };

  // Determine button title
  const getTitle = () => {
    return isFavorite ? t('Remove from favourites') : t('Add to favourites');
  };

  // Determine which icon to show - always use stars
  const IconComponent = isFavorite ? StarIconSolid : StarIcon;

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={clsxm(
        'ml-auto flex-shrink-0 rounded p-1 transition-all duration-200',
        'hover:bg-gray-200 dark:hover:bg-slate-600',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800',
        getVisibilityClass(),
      )}
      title={getTitle()}
      aria-label={getTitle()}
    >
      <IconComponent
        className={clsxm(
          'h-4 w-4 transition-all duration-200',
          isFavorite
            ? 'text-yellow-500 dark:text-yellow-400'
            : isHovered
              ? 'text-yellow-500 dark:text-yellow-400'
              : 'text-gray-400 dark:text-slate-500',
        )}
        aria-hidden='true'
      />
    </button>
  );
};

export default NavPinButton;
